import { useCallback } from "react";
import {
    getPRD5011_R01List,
    getPRD5011_R01ProductResultList,
    exportPRD5011_R01Excel,
    postPRD5011_R01KpidataInsert,
} from "./Api_PRD5011_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { ShowSuccessFetch, showError, showWarning } from "src/utils/toast/toastUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { usePRD5011_R01Store, PRD5011_R01Filter } from "./Store_PRD5011_R01";
import { PRD5011_R01Dto, PRD5011_R01ProductResultDto } from "./Dto_PRD5011_R01";

interface PRD5011_R01ContentProps {
    setMessage: (message: string) => void;
}

const groupRowsByOcrdttm = (rows: PRD5011_R01Dto[]): Map<string, PRD5011_R01Dto[]> => {
    const grouped = new Map<string, PRD5011_R01Dto[]>();

    for (const row of rows) {
        if (!row.ocrdttm) continue;

        const group = grouped.get(row.ocrdttm) ?? [];
        group.push(row);
        grouped.set(row.ocrdttm, group);
    }

    return grouped;
};

/** rowKey(compCd_ocrdttm_kpifldcd)에서 ocrdttm을 yyyy-mm-dd 형태로 변환 */
export const parseOcrdttmFromRowKey = (rowKey: string): string | undefined => {
    const parts = rowKey.split('_');
    if (parts.length < 2) return undefined;

    const ocrdttm = parts[1];
    if (ocrdttm.length !== 8) return undefined;

    return `${ocrdttm.slice(0, 4)}-${ocrdttm.slice(4, 6)}-${ocrdttm.slice(6, 8)}`;
};

export const Content_PRD5011_R01 = (props: PRD5011_R01ContentProps) => {
    const {
		setMessage,
    } = props;

    const { 
		PRD5011_R01List, setPRD5011_R01List,
		setPRD5011_R01ProductResultList,
		initSelectedPRD5011_R01ProductResultList,
		initPRD5011_R01ProductResultData,
		initSelectedPRD5011_R01,
		PRD5011_R01Filter,
		PRD5011_R01PastFilter, setPRD5011_R01PastFilter,
		selectedPRD5011_R01,
		setScrollTop,
		setDetailScrollTop,
	} = usePRD5011_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	useFetchActionbarAuth("PRD5011_R01");

	const getParams = (filter: Partial<PRD5011_R01Filter>) => {
		return {
			compCd: filter.compCd,
			kpicertkey: filter.kpicertkey,
			kpifldcd: filter.kpifldcd,
			ocrdttmDateRange: filter.ocrdttmDateRange,
		};
	}

    const mapProductResultList = (rows: PRD5011_R01ProductResultDto[]) =>
        rows.map((row, idx) => ({
            ...row,
            valueIndex: idx,
            orderIndex: idx + 1,
            orderIndexSeq: 0,
        }));

    const clearProductResultData = useCallback(() => {
        initPRD5011_R01ProductResultData();
    }, [initPRD5011_R01ProductResultData]);

    const fetchProductResultByRow = useCallback(async (row: PRD5011_R01Dto) => {
        const productDt = parseOcrdttmFromRowKey(row.rowKey);
        if (!productDt) {
            showWarning('생산일자 정보를 확인할 수 없습니다.');
            return;
        }

        setLoadingSpinner(true);
        try {
            const response = await getPRD5011_R01ProductResultList({
                productDt,
                compCd: row.compCd,
                kpifldcd: row.kpifldcd,
            });
            const productResultRows = mapProductResultList(
                [...(response.data.data ?? [])] as PRD5011_R01ProductResultDto[]
            );

            setPRD5011_R01ProductResultList(productResultRows);
            initSelectedPRD5011_R01ProductResultList();
        } catch (error) {
            console.error('생산실적 조회 실패:', error);
            showWarning('생산실적 조회에 실패했습니다.');
            initPRD5011_R01ProductResultData();
        } finally {
            setLoadingSpinner(false);
        }
    }, [
        setPRD5011_R01ProductResultList,
        initSelectedPRD5011_R01ProductResultList,
        initPRD5011_R01ProductResultData,
        setLoadingSpinner,
    ]);

    const fetchData = useCallback(async () => {
        setScrollTop(null);
        setDetailScrollTop(null);
        setLoadingSpinner(true);
        try {
            const filterParams = getParams(PRD5011_R01Filter);
            const kpiResponse = await getPRD5011_R01List('', filterParams);

            const newList = [...(kpiResponse.data.data ?? [])] as PRD5011_R01Dto[];

            setPRD5011_R01List(newList);
            initSelectedPRD5011_R01();
            clearProductResultData();

            if (newList.length > 0) {
                setPRD5011_R01PastFilter(PRD5011_R01Filter);
                ShowSuccessFetch(
                    setMessage,
                    `조회가 완료되었습니다. (KPI ${newList.length}건)`
                );
            } else {
                showWarning('데이터가 없습니다.');
            }
        } catch (error) {
            console.error('데이터 조회 실패:', error);
            showWarning('데이터 조회에 실패했습니다.');
            setPRD5011_R01List([]);
            initSelectedPRD5011_R01();
            clearProductResultData();
        } finally {
            setLoadingSpinner(false);
        }
    }, [
        PRD5011_R01Filter,
        setPRD5011_R01List,
        initSelectedPRD5011_R01,
        clearProductResultData,
        setPRD5011_R01PastFilter,
        setMessage,
        setLoadingSpinner,
        setScrollTop,
        setDetailScrollTop,
    ]);
    
    const handleKpiSendClick = useCallback(async () => {
        const ocrdttm = selectedPRD5011_R01?.ocrdttm;

        if (!ocrdttm) {
            showWarning('KPI 전송할 행을 선택하세요.');
            return;
        }

        const targetRows = PRD5011_R01List.filter(row => row.ocrdttm === ocrdttm);

        if (targetRows.length === 0) {
            showWarning('전송할 KPI 데이터가 없습니다.');
            return;
        }

        setLoadingSpinner(true);
        try {
            await postPRD5011_R01KpidataInsert(targetRows);

            const filterParams = getParams(PRD5011_R01Filter);
            const kpiResponse = await getPRD5011_R01List('', filterParams);
            const newList = [...(kpiResponse.data.data ?? [])] as PRD5011_R01Dto[];
            setPRD5011_R01List(newList);

            ShowSuccessFetch(
                setMessage,
                `KPI 전송이 완료되었습니다. (${ocrdttm} / ${targetRows.length}건)`
            );
        } catch (error) {
            console.error('KPI 전송 실패:', error);
            showError('KPI 전송에 실패했습니다.');
        } finally {
            setLoadingSpinner(false);
        }
    }, [
        selectedPRD5011_R01,
        PRD5011_R01List,
        PRD5011_R01Filter,
        setPRD5011_R01List,
        setMessage,
        setLoadingSpinner,
    ]);

    const handleKpiSendAllClick = useCallback(async () => {
        const targetRows = PRD5011_R01List.filter(
            row => row.systmopryn !== 'Y' && row.ocrdttm
        );

        if (targetRows.length === 0) {
            showWarning('전송할 KPI 데이터가 없습니다.');
            return;
        }

        const groupedByOcrdttm = groupRowsByOcrdttm(targetRows);
        const sortedOcrdttmList = Array.from(groupedByOcrdttm.keys()).sort();

        setLoadingSpinner(true);

        const successResults: { ocrdttm: string; count: number }[] = [];
        const failedOcrdttmList: string[] = [];

        try {
            for (const ocrdttm of sortedOcrdttmList) {
                const rows = groupedByOcrdttm.get(ocrdttm)!;

                try {
                    await postPRD5011_R01KpidataInsert(rows);
                    successResults.push({ ocrdttm, count: rows.length });
                } catch (error) {
                    console.error(`KPI 전송 실패 (${ocrdttm}):`, error);
                    failedOcrdttmList.push(ocrdttm);
                    showError(`KPI 전송에 실패했습니다. (${ocrdttm})`);
                }
            }

            const filterParams = getParams(PRD5011_R01Filter);
            const kpiResponse = await getPRD5011_R01List('', filterParams);
            const newList = [...(kpiResponse.data.data ?? [])] as PRD5011_R01Dto[];
            setPRD5011_R01List(newList);

            const successCount = successResults.reduce((sum, result) => sum + result.count, 0);
            const successDays = successResults.length;

            if (successResults.length > 0) {
                const failedMessage = failedOcrdttmList.length > 0
                    ? ` (실패: ${failedOcrdttmList.join(', ')})`
                    : '';

                ShowSuccessFetch(
                    setMessage,
                    `KPI 일괄 전송이 완료되었습니다. (${successDays}일 / ${successCount}건)${failedMessage}`
                );
            } else if (failedOcrdttmList.length > 0) {
                showError(`KPI 일괄 전송에 모두 실패했습니다. (${failedOcrdttmList.join(', ')})`);
            }
        } catch (error) {
            console.error('KPI 일괄 전송 후 목록 갱신 실패:', error);
            showError('목록 갱신에 실패했습니다.');
        } finally {
            setLoadingSpinner(false);
        }
    }, [
        PRD5011_R01List,
        PRD5011_R01Filter,
        setPRD5011_R01List,
        setMessage,
        setLoadingSpinner,
    ]);

    const handleExcelClick = useCallback(async () => {
        const response = await exportPRD5011_R01Excel(
            '',
            getParams(PRD5011_R01Filter)
        );

        downloadSettingExcel({ responseData: response.data.data, fileName: 'KPI 전송 조회-excel.xlsx' });
    }, [PRD5011_R01Filter, PRD5011_R01PastFilter, PRD5011_R01List.length]);
    
    return {
        fetchData,
        handleExcelClick,
        handleKpiSendClick,
        handleKpiSendAllClick,
        fetchProductResultByRow,
        clearProductResultData,
    };

};
